/**
 * Typed Socket.IO singleton.
 *
 * - Connects lazily (`ensureConnected`) and lets Socket.IO auto-retry forever
 *   with capped exponential backoff.
 * - On EVERY (re)connect it calls `notifyConnected()` (net/backend.ts), whose
 *   hook re-joins the current room with the stored seat token (net/session.ts).
 * - Pushes `server:hello`, `room:state` and `room:kicked` into the room store.
 */
import { io, type Socket } from 'socket.io-client';
import type { Activity, ClientToServerEvents, ServerToClientEvents } from '@wordloom/engine';
import { useRoomStore } from '../state/roomStore';
import { notifyConnected, setDefaultBackendFactory, type Backend } from './backend';

export type WordloomSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

let socket: WordloomSocket | null = null;

function wire(s: WordloomSocket): void {
  const store = useRoomStore.getState;

  s.on('connect', () => {
    store().setConnection('connected');
    notifyConnected();
  });

  s.on('disconnect', (reason) => {
    store().markDisconnected();
    store().setConnection('reconnecting');
    // A server-initiated disconnect is not retried automatically by Socket.IO.
    if (reason === 'io server disconnect') s.connect();
  });

  s.on('connect_error', () => {
    store().setConnection(store().everConnected ? 'reconnecting' : 'connecting');
  });

  s.on('server:hello', (hello) => store().receiveHello(hello, Date.now()));
  s.on('room:state', (view) => {
    store().receiveView(view, Date.now());
  });
  s.on('room:kicked', (payload) => store().setKicked(payload));
}

export function getSocket(): WordloomSocket {
  if (!socket) {
    socket = io({
      autoConnect: false,
      reconnection: true,
      reconnectionDelay: 400,
      reconnectionDelayMax: 4000,
      randomizationFactor: 0.4,
      timeout: 10_000,
    });
    wire(socket);
  }
  return socket;
}

export function ensureConnected(): void {
  const s = getSocket();
  if (s.connected || s.active) return;
  useRoomStore.getState().setConnection('connecting');
  s.connect();
}

/** Minimal untyped emitter view used at the generic request boundary. */
interface AckEmitter {
  emit(event: string, ...args: unknown[]): unknown;
}

function socketBackend(): Backend {
  return {
    emit(event, payload, timeoutMs) {
      const s = getSocket();
      return new Promise((resolve, reject) => {
        if (!s.connected) {
          reject(new Error('offline'));
          return;
        }
        // socket.timeout(ms) makes the ack callback receive (err, response).
        const emitter = s.timeout(timeoutMs) as unknown as AckEmitter;
        const callback = (err: Error | null, response: unknown) => {
          if (err) reject(err);
          else resolve(response);
        };
        if (payload === undefined) emitter.emit(event, callback);
        else emitter.emit(event, payload, callback);
      });
    },
    sendActivity(activity: Activity) {
      const s = getSocket();
      // Volatile: an activity hint is worthless once stale, never buffer it.
      if (s.connected) s.volatile.emit('game:activity', { activity });
    },
    connect: ensureConnected,
  };
}

setDefaultBackendFactory(socketBackend);
